import { useWeb3React } from '@web3-react/core';
import { useEffect, useState } from 'react';

import { useAuth } from '@/provider/UserProvider';

import { NetworkKey } from '../constants';
import { useLockContext } from '../LockContext';
import type { LockedDataTable } from './helpers';
import {
  convertToDataTableBsc,
  convertToDataTableOrai,
  getEstimateRewardBsc,
  getEstimateRewardWasm,
  getListHistoryLockedBsc,
  getListHistoryLockedWasm,
  getTotalLockWasm,
  getTotalLockWithUserBsc,
} from './helpers';

const useLockedFetch = () => {
  const { isLoginClient, auth } = useAuth();
  const context = useWeb3React();
  const account = context.account || '';
  const { isRefresh, refreshBalance, network } = useLockContext();
  const [loading, setLoading] = useState<boolean>(false);
  const [list, setList] = useState<LockedDataTable[]>([]);

  useEffect(() => {
    if (network !== NetworkKey.Bsc) return;
    if (account) handleGetListHistoryBsc();
    else setList([]);
  }, [account, isRefresh, network]);

  useEffect(() => {
    if (network !== NetworkKey.Oraichain) return;
    if (isLoginClient) handleGetListHistoryOrai();
    else setList([]);
  }, [isLoginClient, isRefresh, network]);

  const handleWhenClaimed = (nonce: number) => {
    setList((prev) =>
      prev.map((item) => {
        if (item.id === nonce) {
          return { ...item, airi_reward: '0' };
        }
        return item;
      }),
    );
    refreshBalance(network);
  };

  const handleWhenWithdraw = (nonce: number) => {
    setList((prev) =>
      prev.map((item) => {
        if (item.id === nonce) {
          return { ...item, airi_reward: '0', status: true };
        }
        return item;
      }),
    );
    refreshBalance(network);
  };

  const handleGetListHistoryBsc = async () => {
    try {
      setLoading(true);
      const res = await getTotalLockWithUserBsc(account);
      const totalLocked = Number(res);

      if (totalLocked <= 0) {
        setLoading(false);
        setList([]);
        return;
      }

      Promise.all([
        getListHistoryLockedBsc(account, totalLocked),
        getEstimateRewardBsc(account, totalLocked),
      ]).then((values) => {
        const [history, rewards] = values;
        const result = convertToDataTableBsc(rewards, history);
        const dataNewest = result.reverse();
        setList(dataNewest);
      });
    } catch (error) {
      setLoading(false);
      console.log('get list locked err: ', error);
    }
  };

  const handleGetListHistoryOrai = async () => {
    try {
      setLoading(true);
      const res = await getTotalLockWasm(auth.user.publicAddress);
      const totalLocked = res.data;

      if (totalLocked <= 0) {
        setLoading(false);
        setList([]);
        return;
      }

      Promise.all([
        getListHistoryLockedWasm(auth.user.publicAddress, totalLocked),
        getEstimateRewardWasm(auth.user.publicAddress, totalLocked),
      ]).then((values) => {
        const [history, rewards] = values;
        const result = convertToDataTableOrai(rewards, history);
        const dataNewest = result.reverse();
        setList(dataNewest);
      });
    } catch (error) {
      setLoading(false);
      console.log('get list locked err: ', error);
    }
  };

  return {
    list,
    loading,
    handleWhenClaimed,
    handleWhenWithdraw,
  };
};

export default useLockedFetch;
